import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { Toaster } from './components/ui/sonner';
import ProtectedRoute from './components/ProtectedRoute';
import RoleProtectedRoute from './components/RoleProtectedRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import DataUploadRES from './pages/DataUploadRES';
import UploadHistory from './pages/UploadHistory';
import AgentResults from './pages/AgentResults';
import AgentRecommendations from './pages/AgentRecommendations';
import Agents from './pages/Agents';
import Chatbot from './pages/ChatbotNew';
import ChatbotMultilingual from './pages/ChatbotMultilingual';
import ExecutiveSummary from './pages/ExecutiveSummary';
import Forecasts from './pages/Forecasts';
import CreateCompany from './pages/CreateCompany';
import SuperAdminCompanies from './pages/SuperAdminCompanies';
import ManageProfile from './pages/ManageProfile';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import SuperAdminGlobalUsers from './pages/SuperAdminGlobalUsers';
import PlatformMonitoring from './pages/PlatformMonitoring';
import AdminUsers from './pages/AdminUsers';
import CompanySettings from './pages/CompanySettings';
import AuditLog from './pages/AuditLog';
import FootfallVsSales from './pages/FootfallVsSales';
import TargetVsAchievement from './pages/TargetVsAchievement';
import AlertSettings from './pages/AlertSettings';
import StockSenseDashboard from './pages/StockSenseDashboard';
import SalesIQDashboard from './pages/SalesIQDashboard';
import Customer360Dashboard from './pages/Customer360Dashboard';
import ProductMixDashboard from './pages/ProductMixDashboard';
import VendorLinkDashboard from './pages/VendorLinkDashboard';
import StorePulseDashboard from './pages/StorePulseDashboard';
import ExecutiveBrainDashboard from './pages/ExecutiveBrainDashboard';
import PromoIQDashboard from './pages/PromoIQDashboard';
import ReturnIQDashboard from './pages/ReturnIQDashboard';
import DemandFlowDashboard from './pages/DemandFlowDashboard';
import CommercialInterventionDashboard from './pages/CommercialInterventionDashboard';
import ForecastComparison from './pages/ForecastComparison';
import Tasks from './pages/Tasks';
import '@/App.css';

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <BrowserRouter>
          <div className="App">
            <Routes>
            <Route path="/" element={<Login />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              {/* Business Application Routes */}
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="upload-res" element={<DataUploadRES />} />
              <Route path="upload-history" element={<UploadHistory />} />
              <Route path="agents" element={<Agents />} />
              <Route path="agent-results" element={<AgentResults />} />
              <Route path="agent-recommendations" element={<AgentRecommendations />} />
              <Route path="executive-summary" element={<ExecutiveSummary />} />
              <Route path="forecasts" element={<Forecasts />} />
              <Route path="footfall-vs-sales" element={<FootfallVsSales />} />
              <Route path="target-vs-achievement" element={<TargetVsAchievement />} />
              <Route path="dashboard/stock-sense" element={<StockSenseDashboard />} />
              <Route path="dashboard/sales-iq" element={<SalesIQDashboard />} />
              <Route path="dashboard/customer-360" element={<Customer360Dashboard />} />
              <Route path="dashboard/product-mix" element={<ProductMixDashboard />} />
              <Route path="dashboard/vendor-link" element={<VendorLinkDashboard />} />
              <Route path="dashboard/store-pulse" element={<StorePulseDashboard />} />
              <Route path="dashboard/executive-brain" element={<ExecutiveBrainDashboard />} />
              <Route path="dashboard/promo-iq" element={<PromoIQDashboard />} />
              <Route path="dashboard/return-iq" element={<ReturnIQDashboard />} />
              <Route path="dashboard/demand-flow" element={<DemandFlowDashboard />} />
              <Route path="dashboard/commercial-intervention" element={<CommercialInterventionDashboard />} />
              <Route path="forecast-comparison" element={<ForecastComparison />} />
              <Route path="chatbot" element={<ChatbotMultilingual />} />
              <Route path="chatbot-legacy" element={<Chatbot />} />
              <Route path="manage-profile" element={<ManageProfile />} />
              <Route path="tasks" element={<Tasks />} />
              
              {/* SuperAdmin Routes - Platform Administration */}
              <Route path="superadmin/monitoring" element={
                <RoleProtectedRoute allowedRoles={['superadmin']}>
                  <PlatformMonitoring />
                </RoleProtectedRoute>
              } />
              <Route path="superadmin/users" element={
                <RoleProtectedRoute allowedRoles={['superadmin']}>
                  <SuperAdminGlobalUsers />
                </RoleProtectedRoute>
              } />
              <Route path="superadmin/companies" element={
                <RoleProtectedRoute allowedRoles={['superadmin']}>
                  <SuperAdminCompanies />
                </RoleProtectedRoute>
              } />
              <Route path="super-admin/dashboard" element={
                <RoleProtectedRoute allowedRoles={['superadmin']}>
                  <SuperAdminDashboard />
                </RoleProtectedRoute>
              } />
              <Route path="super-admin/manage-companies" element={
                <RoleProtectedRoute allowedRoles={['superadmin']}>
                  <SuperAdminCompanies />
                </RoleProtectedRoute>
              } />
              <Route path="super-admin/create-company" element={
                <RoleProtectedRoute allowedRoles={['superadmin']}>
                  <CreateCompany />
                </RoleProtectedRoute>
              } />
              
              {/* Admin Routes - Company Administration */}
              <Route path="admin/users" element={
                <RoleProtectedRoute allowedRoles={['admin', 'company_admin', 'superadmin']}>
                  <AdminUsers />
                </RoleProtectedRoute>
              } />
              <Route path="admin/settings" element={
                <RoleProtectedRoute allowedRoles={['admin', 'company_admin', 'superadmin']}>
                  <CompanySettings />
                </RoleProtectedRoute>
              } />
              <Route path="admin/audit-log" element={
                <RoleProtectedRoute allowedRoles={['admin', 'company_admin', 'superadmin']}>
                  <AuditLog />
                </RoleProtectedRoute>
              } />
              <Route path="admin/alert-settings" element={
                <RoleProtectedRoute allowedRoles={['admin', 'company_admin', 'superadmin']}>
                  <AlertSettings />
                </RoleProtectedRoute>
              } />
              
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
          </Routes>
          <Toaster position="top-right" richColors />
        </div>
      </BrowserRouter>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
